import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * A resource identifier with a copy button.
 *
 * This is developer infrastructure: an id is something you paste into a config
 * file, a support ticket or a curl command, and selecting monospace text by hand
 * is a small tax paid dozens of times a day.
 */
export function CopyableId({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1_500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-mono text-[12px] text-[var(--vc-text-muted)]">{value}</span>
      <button
        type="button"
        // Names the resource, so a screen-reader user hears "Copy product id"
        // rather than an unqualified "Copy" repeated down a page.
        aria-label={`Copy ${label ?? 'identifier'}`}
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => setCopied(true));
        }}
        className={
          'rounded-[var(--vc-radius-sm)] p-1 text-[var(--vc-text-subtle)] transition-colors ' +
          'duration-[var(--vc-duration-fast)] hover:bg-[var(--vc-surface-hover)] ' +
          'hover:text-[var(--vc-text)] focus-visible:outline-2 focus-visible:outline-offset-1 ' +
          'focus-visible:outline-[var(--vc-focus-ring)]'
        }
      >
        {copied ? (
          <Check aria-hidden="true" className="size-3.5 text-[var(--vc-success-text)]" />
        ) : (
          <Copy aria-hidden="true" className="size-3.5" />
        )}
      </button>
      {/* Announced once when the copy happens, rather than on every render. */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </span>
  );
}
